import React, { useState } from "react";
import { toast } from "react-hot-toast";
import {
  IconBriefcase,
  IconUsers,
  IconSearch,
  IconPlus,
  IconExternalLink,
  IconCalendar,
  IconMail,
  IconClock,
  IconCircleCheck,
  IconCircleX,
  IconLayoutKanban,
  IconList,
  IconStar,
  IconFileText,
  IconSend,
  IconDownload,
  IconVideo,
  IconCheck,
  IconX,
  IconBuilding,
  IconPhone,
  IconChevronRight,
  IconLoader2,
  IconSparkles,
  IconAlertCircle,
  IconTrash
} from "@tabler/icons-react";
import RoleToggle from "../../../components/RoleToggle";

type Job = {
  id: string;
  title: string;
  department: string;
  location: string;
  salary: string;
  status: "Approved" | "In Approval";
  approvals: { manager: string; hr: string; admin: string };
  postedChannels: string[];
  description: string;
};

type Candidate = {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  matchScore: number;
  skills: string[];
  source: string;
  stage: "applied" | "screening" | "interview" | "offer" | "hired" | "rejected";
  
  // Screening fields
  screeningChecklist?: {
    skillsMatch: boolean;
    salaryFit: boolean;
    locationReady: boolean;
    communicationReady: boolean;
  };
  screeningNotes?: string;

  // Interview fields
  interviewDetails?: {
    date: string;
    time: string;
    type: "Zoom" | "Google Meet" | "In-Person";
    round: string;
    interviewer: string;
    link: string;
    feedbackScore?: number;
    feedbackNotes?: string;
    status: "Pending" | "Scheduled" | "Completed" | "Failed";
  };

  // Offer fields
  offerDetails?: {
    ctc: string;
    joiningDate: string;
    manager: string;
    probation: string;
    template: string;
    status: "Draft" | "Generated" | "Sent" | "Accepted";
    language?: string;
    currency?: string;
  };
};

const INITIAL_JOBS: Job[] = [
  { 
    id: "JOB-001", 
    title: "Senior React Developer", 
    department: "Engineering", 
    location: "Baner Branch", 
    salary: "₹18L - ₹24L", 
    status: "Approved", 
    approvals: { manager: "Approved", hr: "Approved", admin: "Pending" },
    postedChannels: ["LinkedIn", "Indeed"],
    description: "Looking for a Senior React Developer with 5+ years of experience in building modern single-page applications. Must be proficient in TypeScript and state management libraries like Redux or Zustand."
  },
  { 
    id: "JOB-002", 
    title: "HR Generalist", 
    department: "Human Resources", 
    location: "Main Head Office", 
    salary: "₹6L - ₹8L", 
    status: "In Approval", 
    approvals: { manager: "Approved", hr: "Pending", admin: "Pending" },
    postedChannels: ["Indeed"],
    description: "Seeking an HR Generalist to handle end-to-end recruitment, employee onboarding, policies creation, and engagement initiatives."
  },
  { 
    id: "JOB-003", 
    title: "DevOps Engineer", 
    department: "IT & Support", 
    location: "Remote", 
    salary: "₹12L - ₹16L", 
    status: "Approved", 
    approvals: { manager: "Approved", hr: "Approved", admin: "Approved" },
    postedChannels: ["LinkedIn", "Indeed", "Naukri"],
    description: "DevOps Engineer with experience in AWS cloud infrastructure management, Terraform infrastructure-as-code, and Docker/Kubernetes configuration."
  },
];

const INITIAL_CANDIDATES: Candidate[] = [
  { 
    id: "C-001", 
    name: "Amit Sharma", 
    email: "amit.sharma@example.com",
    phone: "+91 98765 43210",
    role: "Senior React Developer", 
    matchScore: 94, 
    skills: ["React", "TypeScript", "Tailwind", "Redux"], 
    source: "LinkedIn",
    stage: "applied"
  },
  { 
    id: "C-002", 
    name: "Ruchi Patel", 
    email: "ruchi.patel@example.com",
    phone: "+91 87654 32109",
    role: "HR Generalist", 
    matchScore: 82, 
    skills: ["Recruiting", "Onboarding", "Excel", "HRIS"], 
    source: "Indeed",
    stage: "applied"
  },
  { 
    id: "C-003", 
    name: "Vikram Malhotra", 
    email: "vikram.m@example.com",
    phone: "+91 76543 21098",
    role: "DevOps Engineer", 
    matchScore: 89, 
    skills: ["AWS", "Docker", "Kubernetes", "CI/CD"], 
    source: "Naukri",
    stage: "screening",
    screeningChecklist: { skillsMatch: true, salaryFit: true, locationReady: false, communicationReady: true },
    screeningNotes: "Strong containerization skills. Needs review on hybrid work model expectations."
  },
  { 
    id: "C-004", 
    name: "Sneha Nair", 
    email: "sneha.nair@example.com",
    phone: "+91 65432 10987",
    role: "Senior React Developer", 
    matchScore: 91, 
    skills: ["React", "Redux", "Node.js", "GraphQL"], 
    source: "LinkedIn",
    stage: "interview",
    interviewDetails: {
      date: "2026-05-22",
      time: "10:00 AM",
      type: "Zoom",
      round: "Technical Round 1",
      interviewer: "Amit Patel (Tech Lead)",
      link: "https://zoom.us/j/8492049102",
      status: "Scheduled"
    }
  },
  { 
    id: "C-005", 
    name: "Rohan Das", 
    email: "rohan.das@example.com",
    phone: "+91 54321 09876",
    role: "DevOps Engineer", 
    matchScore: 95, 
    skills: ["Terraform", "CI/CD", "AWS", "Python"], 
    source: "Direct Referral",
    stage: "offer",
    offerDetails: {
      ctc: "15,00,000",
      joiningDate: "2026-06-15",
      manager: "Rajesh Kumar (VP Eng)",
      probation: "3 Months",
      template: "Tech",
      status: "Draft"
    }
  },
];

export default function Recruitment() {
  const [activeSubView, setActiveSubView] = useState<"ats" | "jobs" | "screening" | "interviews" | "offers">("jobs");
  const [jobs, setJobs] = useState<Job[]>(INITIAL_JOBS);
  const [candidates, setCandidates] = useState<Candidate[]>(INITIAL_CANDIDATES);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);

  const subViewTabs = [
    "Job Posting",
    "ATS Kanban",
    "Screening Hub",
    "Interview Loop",
    "Offer Letters"
  ];
  const tabToViewMap: Record<string, "jobs" | "ats" | "screening" | "interviews" | "offers"> = {
    "Job Posting": "jobs",
    "ATS Kanban": "ats",
    "Screening Hub": "screening",
    "Interview Loop": "interviews",
    "Offer Letters": "offers"
  };
  const viewToTabMap: Record<string, string> = {
    jobs: "Job Posting",
    ats: "ATS Kanban",
    screening: "Screening Hub",
    interviews: "Interview Loop",
    offers: "Offer Letters"
  };

  // Search & Filter
  const [jobSearch, setJobSearch] = useState("");
  
  // Job Creation Modal State
  const [isCreateJobOpen, setIsCreateJobOpen] = useState(false);
  const [newJobTitle, setNewJobTitle] = useState("");
  const [newJobDept, setNewJobDept] = useState("Engineering");
  const [newJobLoc, setNewJobLoc] = useState("Baner Branch");
  const [newJobSalary, setNewJobSalary] = useState("₹12L - ₹18L");
  const [newJobDesc, setNewJobDesc] = useState("");
  const [newJobChannels, setNewJobChannels] = useState<string[]>(["LinkedIn"]);

  // Posting Progress Loader Modal
  const [isPostingJob, setIsPostingJob] = useState(false);
  const [postingStep, setPostingStep] = useState(0);

  // Job posting channels list
  const CHANNELS = ["LinkedIn", "Indeed", "Naukri", "Glassdoor"];

  // Helper to move candidate and automatically open/focus the next stage screen
  const handleTransitionCandidate = (id: string, nextStage: Candidate["stage"], focusView?: typeof activeSubView) => {
    setCandidates(prev => prev.map(c => {
      if (c.id === id) {
        let updated = { ...c, stage: nextStage };
        // Pre-initialize empty structures if moving to stages that need them
        if (nextStage === "screening" && !c.screeningChecklist) {
          updated.screeningChecklist = { skillsMatch: false, salaryFit: false, locationReady: false, communicationReady: false };
        }
        if (nextStage === "interview" && !c.interviewDetails) {
          updated.interviewDetails = {
            date: "", time: "", type: "Zoom", round: "Technical Round 1", interviewer: "Amit Patel (Tech Lead)", link: "", status: "Pending"
          };
        }
        if (nextStage === "offer" && !c.offerDetails) {
          updated.offerDetails = {
            ctc: "12,00,000", joiningDate: new Date(Date.now() + 14*24*60*60*1000).toISOString().split('T')[0],
            manager: "Rajesh Kumar (VP Eng)", probation: "3 Months", template: "Standard", status: "Draft"
          };
        }
        return updated;
      }
      return c;
    }));

    if (focusView) {
      setSelectedCandidateId(id);
      setActiveSubView(focusView);
    }
  };

  // 1. Create Job Requisition Flow
  const triggerCreateJob = () => {
    if (!newJobTitle.trim()) {
      toast.error("Please enter a job title");
      return;
    }

    setIsCreateJobOpen(false);
    setIsPostingJob(true);
    setPostingStep(1);

    // Simulate multi-channel posting progress step-by-step
    setTimeout(() => {
      setPostingStep(2);
      setTimeout(() => {
        setPostingStep(3);
        setTimeout(() => {
          setPostingStep(4);
          setTimeout(() => {
            // Done posting, save to state
            const newJobRecord: Job = {
              id: `JOB-00${jobs.length + 1}`,
              title: newJobTitle,
              department: newJobDept,
              location: newJobLoc,
              salary: newJobSalary,
              status: "Approved",
              approvals: { manager: "Approved", hr: "Approved", admin: "Approved" },
              postedChannels: newJobChannels,
              description: newJobDesc || "No description provided."
            };

            setJobs(prev => [newJobRecord, ...prev]);
            setIsPostingJob(false);
            setPostingStep(0);
            toast.success("Job Requisition Posted & Syndicated Live!");

            // Reset form
            setNewJobTitle("");
            setNewJobDesc("");
            setNewJobChannels(["LinkedIn"]);
          }, 800);
        }, 800);
      }, 800);
    }, 800);
  };

  // Toggle posted channel from the table
  const handleToggleChannel = (jobId: string, channel: string) => {
    const activeJob = jobs.find(j => j.id === jobId);
    if (!activeJob) return;

    const isPosted = activeJob.postedChannels.includes(channel);
    toast.loading(`${isPosted ? 'Removing' : 'Posting'} job on ${channel}...`, { id: "channelToggle" });

    setTimeout(() => {
      setJobs(prev => prev.map(j => {
        if (j.id === jobId) {
          const updatedChannels = isPosted 
            ? j.postedChannels.filter(c => c !== channel)
            : [...j.postedChannels, channel];
          return { ...j, postedChannels: updatedChannels };
        }
        return j;
      }));
      toast.success(isPosted ? `Job removed from ${channel}` : `Job successfully posted to ${channel}!`, { id: "channelToggle" });
    }, 1000);
  };

  // 2. Resume Screening Update
  const updateScreeningChecklist = (candId: string, key: keyof Required<Candidate>["screeningChecklist"]) => {
    setCandidates(prev => prev.map(c => {
      if (c.id === candId) {
        const checklist = c.screeningChecklist || { skillsMatch: false, salaryFit: false, locationReady: false, communicationReady: false };
        return {
          ...c,
          screeningChecklist: {
            ...checklist,
            [key]: !checklist[key]
          }
        };
      }
      return c;
    }));
  };

  const updateScreeningNotes = (candId: string, notes: string) => {
    setCandidates(prev => prev.map(c => {
      if (c.id === candId) {
        return { ...c, screeningNotes: notes };
      }
      return c;
    }));
  };

  // 3. Interview Scheduling & Meeting Link Generator
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const handleGenerateMeetingLink = (candId: string) => {
    setIsGeneratingLink(true);
    setTimeout(() => {
      setCandidates(prev => prev.map(c => {
        if (c.id === candId) {
          const details = c.interviewDetails || {
            date: "", time: "", type: "Zoom", round: "Technical Round 1", interviewer: "", link: "", status: "Pending"
          };
          return {
            ...c,
            interviewDetails: {
              ...details,
              link: `https://meet.google.com/gwm-${Math.random().toString(36).substring(2, 5)}-${Math.random().toString(36).substring(2, 6)}`
            }
          };
        }
        return c;
      }));
      setIsGeneratingLink(false);
      toast.success("Secure Google Meet Link generated!");
    }, 900);
  };

  const handleScheduleInterview = (candId: string) => {
    const c = candidates.find(cand => cand.id === candId);
    if (!c || !c.interviewDetails?.date || !c.interviewDetails?.time) {
      toast.error("Please fill in Date and Time");
      return;
    }

    toast.loading("Sending calendar invites to candidate & interviewer...", { id: "schedule" });
    setTimeout(() => {
      setCandidates(prev => prev.map(cand => {
        if (cand.id === candId) {
          return {
            ...cand,
            interviewDetails: {
              ...cand.interviewDetails!,
              status: "Scheduled"
            }
          };
        }
        return cand;
      }));
      toast.success("Interview scheduled & email invitations sent!", { id: "schedule" });
    }, 1200);
  };

  const handleUpdateInterviewField = (candId: string, key: keyof Required<Candidate>["interviewDetails"], value: any) => {
    setCandidates(prev => prev.map(c => {
      if (c.id === candId) {
        const details = c.interviewDetails || {
          date: "", time: "", type: "Zoom", round: "Technical Round 1", interviewer: "", link: "", status: "Pending"
        };
        return {
          ...c,
          interviewDetails: {
            ...details,
            [key]: value
          }
        };
      }
      return c;
    }));
  };

  // 4. Offer Generation & Email/PDF triggers
  const handleUpdateOfferField = (candId: string, key: keyof Required<Candidate>["offerDetails"], value: any) => {
    setCandidates(prev => prev.map(c => {
      if (c.id === candId) {
        const details = c.offerDetails || {
          ctc: "12,00,000", joiningDate: "", manager: "", probation: "3 Months", template: "Standard", status: "Draft"
        };
        return {
          ...c,
          offerDetails: {
            ...details,
            [key]: value
          }
        };
      }
      return c;
    }));
  };

  const [isSendingOffer, setIsSendingOffer] = useState(false);
  const handleSendOfferEmail = (candId: string) => {
    setIsSendingOffer(true);
    setTimeout(() => {
      setCandidates(prev => prev.map(c => {
        if (c.id === candId && c.offerDetails) {
          return {
            ...c,
            offerDetails: {
              ...c.offerDetails,
              status: "Sent"
            }
          };
        }
        return c;
      }));
      setIsSendingOffer(false);
      toast.success("Offer Letter successfully dispatched to Candidate's Email!");
    }, 2000);
  };

  const handleDownloadOfferPdf = (candName: string) => {
    const id = toast.loading("Formatting layout and rendering fonts...");
    setTimeout(() => {
      toast.loading("Generating digital signatures and certificates...", { id });
      setTimeout(() => {
        toast.success(`Download Complete: Offer_Letter_${candName.replace(/\s+/g, "_")}.pdf`, { id });
      }, 1000);
    }, 1000);
  };

  const [isAcceptingOffer, setIsAcceptingOffer] = useState(false);
  const handleAcceptOfferMock = (candId: string) => {
    setIsAcceptingOffer(true);
    setTimeout(() => {
      setCandidates(prev => prev.map(c => {
        if (c.id === candId && c.offerDetails) {
          return {
            ...c,
            stage: "hired",
            offerDetails: {
              ...c.offerDetails,
              status: "Accepted"
            }
          };
        }
        return c;
      }));
      setIsAcceptingOffer(false);
      toast.success("Candidate has ACCEPTED the offer! Moving to Onboarded status.");
      setActiveSubView("ats");
    }, 1500);
  };

  // Filter candidates & jobs for listings
  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(jobSearch.toLowerCase()) ||
    job.department.toLowerCase().includes(jobSearch.toLowerCase())
  );

  return (
    <div className="w-full mx-auto mt-2 animate-in fade-in duration-300">
      
      {/* Header and Sub Tabs Navigation */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5 px-2">
        <div>
          <h1 className="text-2xl font-bold text-[#212121] flex items-center gap-2">
            <span className="w-2 h-6 rounded-full bg-[#FBD037]"></span>
            Recruitment & ATS Portal
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">Manage job requisitions, track pipelines, schedule interviews, and generate offer letters.</p>
        </div>

        <RoleToggle
          options={subViewTabs}
          active={viewToTabMap[activeSubView]}
          onChange={(tab) => {
            const nextView = tabToViewMap[tab];
            setActiveSubView(nextView);
            if (nextView === "screening") {
              const firstScreening = candidates.find(c => c.stage === "screening");
              if (firstScreening) setSelectedCandidateId(firstScreening.id);
            } else if (nextView === "interviews") {
              const firstInterview = candidates.find(c => c.stage === "interview");
              if (firstInterview) setSelectedCandidateId(firstInterview.id);
            } else if (nextView === "offers") {
              const firstOffer = candidates.find(c => c.stage === "offer");
              if (firstOffer) setSelectedCandidateId(firstOffer.id);
            }
          }}
        />
      </div>
      {activeSubView === "jobs" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center px-2 flex-wrap gap-3">
            <div className="relative">
              <IconSearch size={16} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs & depts..."
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
                className="pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm outline-none w-64 shadow-sm"
              />
            </div>
            <button 
              onClick={() => setIsCreateJobOpen(true)}
              className="bg-[#FBD037] text-[#212121] px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm font-semibold hover:bg-[#FACC15] transition shadow-sm"
            >
              <IconPlus size={16} /> Create Requisition
            </button>
          </div>

          {/* Jobs Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-[#F7F6F2]">
                    <th className="px-6 py-4 font-bold text-[#212121]">Job Title</th>
                    <th className="px-6 py-4 font-bold text-[#212121]">Department</th>
                    <th className="px-6 py-4 font-bold text-[#212121]">Location</th>
                    <th className="px-6 py-4 font-bold text-[#212121]">Salary Range</th>
                    <th className="px-6 py-4 font-bold text-[#212121]">Approval Stage</th>
                    <th className="px-6 py-4 font-bold text-[#212121]">Status</th>
                    <th className="px-6 py-4 font-bold text-[#212121] text-center">Channels (Click to Toggle)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-gray-400">
                        <IconBriefcase size={36} className="mx-auto mb-2 text-gray-300" />
                        No job openings found.
                      </td>
                    </tr>
                  ) : (
                    filteredJobs.map((job) => (
                      <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition">
                        <td className="px-6 py-4">
                          <div className="font-semibold text-gray-900">{job.title}</div>
                          <div className="text-[10px] text-gray-400 font-mono mt-0.5">{job.id}</div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 font-medium">{job.department}</td>
                        <td className="px-6 py-4 text-gray-600">
                          <div className="flex items-center gap-1">
                            <IconBuilding size={14} className="text-gray-400" />
                            {job.location}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 font-mono">{job.salary}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 text-[10px]">
                            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">Mgr ✔</span>
                            <span className={`px-2 py-0.5 rounded-full ${
                              job.approvals.hr === "Approved" ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-amber-50 text-amber-600 border border-amber-200"
                            }`}>{job.approvals.hr === "Approved" ? "HR ✔" : "HR ⌛"}</span>
                            <span className={`px-2 py-0.5 rounded-full ${
                              job.approvals.admin === "Approved" ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-gray-50 text-gray-500 border border-gray-200"
                            }`}>{job.approvals.admin === "Approved" ? "Adm ✔" : "Adm ⌛"}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                            job.status === "Approved" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-amber-50 text-amber-700 border border-amber-100"
                          }`}>{job.status}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            {CHANNELS.map((ch) => {
                              const active = job.postedChannels.includes(ch);
                              return (
                                <button
                                  key={ch}
                                  onClick={() => handleToggleChannel(job.id, ch)}
                                  className={`text-[10px] font-semibold px-2 py-1 rounded-md border transition-all ${
                                    active
                                      ? ch === "LinkedIn" ? "bg-blue-50 text-blue-600 border-blue-200 font-bold hover:bg-blue-100"
                                        : ch === "Indeed" ? "bg-red-50 text-red-600 border-red-200 font-bold hover:bg-red-100"
                                        : ch === "Naukri" ? "bg-amber-50 text-amber-700 border-amber-200 font-bold hover:bg-amber-100"
                                        : "bg-green-50 text-green-700 border-green-200 font-bold hover:bg-green-100"
                                      : "bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100 hover:text-gray-600"
                                  }`}
                                >
                                  {ch}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* -------------------- 2. ATS KANBAN VIEW -------------------- */}
      {activeSubView === "ats" && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          
          {/* Column 1: Applied Intake */}
          <div className="bg-gray-50/70 rounded-2xl p-3 border border-gray-200/60 flex flex-col min-h-[500px]">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">1. Applied Intake</span>
              <span className="bg-gray-200/80 text-gray-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                {candidates.filter(c => c.stage === "applied").length}
              </span>
            </div>
            <div className="space-y-3 flex-grow overflow-y-auto max-h-[600px] pr-1">
              {candidates.filter(c => c.stage === "applied").map((c) => (
                <div key={c.id} className="bg-white rounded-xl border border-gray-200/80 p-3 shadow-sm hover:border-[#FBD037] hover:shadow-md transition duration-200">
                  <div className="flex justify-between items-start">
                    <h4 className="font-bold text-gray-900 text-[13px]">{c.name}</h4>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                      {c.matchScore}% Match
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{c.role}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {c.skills.map((s, i) => (
                      <span key={i} className="text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{s}</span>
                    ))}
                  </div>
                  <div className="mt-3 pt-2.5 border-t border-gray-100 flex justify-between items-center text-[10px] text-gray-400">
                    <span>via {c.source}</span>
                    <button 
                      onClick={() => handleTransitionCandidate(c.id, "screening", "screening")}
                      className="text-[#212121] bg-[#FBD037] hover:bg-[#FACC15] font-bold px-2.5 py-1 rounded-lg transition text-[10px] shadow-sm flex items-center gap-0.5"
                    >
                      Screen <IconChevronRight size={10} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Column 2: Screening */}
          <div className="bg-gray-50/70 rounded-2xl p-3 border border-gray-200/60 flex flex-col min-h-[500px]">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">2. Screening</span>
              <span className="bg-gray-200/80 text-gray-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                {candidates.filter(c => c.stage === "screening").length}
              </span>
            </div>
            <div className="space-y-3 flex-grow overflow-y-auto max-h-[600px] pr-1">
              {candidates.filter(c => c.stage === "screening").map((c) => (
                <div key={c.id} className="bg-white rounded-xl border border-gray-200/80 p-3 shadow-sm hover:border-[#FBD037] hover:shadow-md transition duration-200">
                  <div className="flex justify-between items-start">
                    <h4 className="font-bold text-gray-900 text-[13px]">{c.name}</h4>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                      {c.matchScore}% Match
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{c.role}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {c.skills.map((s, i) => (
                      <span key={i} className="text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{s}</span>
                    ))}
                  </div>
                  <div className="mt-3.5 pt-2 border-t border-gray-100 flex gap-2">
                    <button 
                      onClick={() => {
                        handleTransitionCandidate(c.id, "rejected");
                        toast.error("Candidate moved to Rejected archive");
                      }}
                      className="flex-1 text-[10px] font-bold py-1.5 rounded-lg border border-gray-200 hover:bg-red-50 hover:text-red-600 text-gray-500 transition"
                    >
                      Reject
                    </button>
                    <button 
                      onClick={() => handleTransitionCandidate(c.id, "interview", "interviews")}
                      className="flex-1 text-[10px] font-bold py-1.5 bg-[#FBD037] text-[#212121] hover:bg-[#FACC15] rounded-lg transition text-center shadow-sm"
                    >
                      Schedule
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Column 3: Interviewing */}
          <div className="bg-gray-50/70 rounded-2xl p-3 border border-gray-200/60 flex flex-col min-h-[500px]">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">3. Interviewing</span>
              <span className="bg-gray-200/80 text-gray-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                {candidates.filter(c => c.stage === "interview").length}
              </span>
            </div>
            <div className="space-y-3 flex-grow overflow-y-auto max-h-[600px] pr-1">
              {candidates.filter(c => c.stage === "interview").map((c) => (
                <div key={c.id} className="bg-white rounded-xl border border-gray-200/80 p-3 shadow-sm hover:border-[#FBD037] hover:shadow-md transition duration-200">
                  <div className="flex justify-between items-start">
                    <h4 className="font-bold text-gray-900 text-[13px]">{c.name}</h4>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                      {c.matchScore}% Match
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{c.role}</p>
                  
                  {c.interviewDetails?.status === "Scheduled" ? (
                    <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-100 flex items-start gap-1 text-[9px] text-amber-800">
                      <IconCalendar size={11} className="shrink-0 mt-0.5" />
                      <div>
                        <strong>{c.interviewDetails.round}</strong>
                        <div>{c.interviewDetails.date} @ {c.interviewDetails.time}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 p-2 rounded bg-gray-50 border border-gray-200/80 flex items-center gap-1 text-[9px] text-gray-500">
                      <IconClock size={11} />
                      <span>Pending interview setup</span>
                    </div>
                  )}

                  <div className="mt-3 pt-2.5 border-t border-gray-100 flex gap-2">
                    <button 
                      onClick={() => {
                        handleTransitionCandidate(c.id, "rejected");
                        toast.error("Candidate failed interview loop");
                      }}
                      className="flex-1 text-[10px] font-bold py-1.5 rounded-lg border border-gray-200 hover:bg-red-50 hover:text-red-600 text-gray-500 transition"
                    >
                      Fail Loop
                    </button>
                    <button 
                      onClick={() => handleTransitionCandidate(c.id, "offer", "offers")}
                      className="flex-1 text-[10px] font-bold py-1.5 bg-[#FBD037] text-[#212121] hover:bg-[#FACC15] rounded-lg transition text-center shadow-sm"
                    >
                      Make Offer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Column 4: Offer Sent */}
          <div className="bg-gray-50/70 rounded-2xl p-3 border border-gray-200/60 flex flex-col min-h-[500px]">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">4. Offer Sent</span>
              <span className="bg-gray-200/80 text-gray-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                {candidates.filter(c => c.stage === "offer").length}
              </span>
            </div>
            <div className="space-y-3 flex-grow overflow-y-auto max-h-[600px] pr-1">
              {candidates.filter(c => c.stage === "offer").map((c) => (
                <div key={c.id} className="bg-white rounded-xl border border-gray-200/80 p-3.5 shadow-sm hover:border-[#FBD037] hover:shadow-md transition">
                  <div className="flex justify-between items-start">
                    <h4 className="font-bold text-gray-900 text-[13px]">{c.name}</h4>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                      {c.matchScore}% Match
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{c.role}</p>

                  <div className="mt-2.5 p-2 rounded bg-blue-50 border border-blue-100 flex flex-col gap-0.5 text-[9px] text-blue-800">
                    <div className="flex items-center gap-1 font-bold">
                      <IconFileText size={11} />
                      <span>Offer Status: {c.offerDetails?.status || "Draft"}</span>
                    </div>
                    {c.offerDetails?.ctc && (
                      <div>Salary Package: ₹{parseFloat(c.offerDetails.ctc.replace(/,/g, "")).toLocaleString("en-IN")} LPA</div>
                    )}
                  </div>
                  
                  <div className="mt-3.5 pt-2 border-t border-gray-100 flex gap-1.5">
                    <button 
                      onClick={() => handleTransitionCandidate(c.id, "hired", "offers")}
                      className="w-full text-[11px] font-bold py-2 bg-black text-[#FBD037] hover:bg-gray-900 rounded-xl transition text-center flex items-center justify-center gap-1 shadow-md"
                    >
                      <IconCircleCheck size={13} /> Process & Onboard
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Column 5: Onboarded (Hired) */}
          <div className="bg-gray-50/70 rounded-2xl p-3 border border-gray-200/60 flex flex-col min-h-[500px]">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">5. Onboarded</span>
              <span className="bg-gray-200/80 text-gray-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                {candidates.filter(c => c.stage === "hired").length}
              </span>
            </div>
            <div className="space-y-3 flex-grow overflow-y-auto max-h-[600px] pr-1">
              {candidates.filter(c => c.stage === "hired").length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20">
                  <IconUsers className="text-gray-300 mb-2" size={32} />
                  <span className="text-xs text-gray-400">No hires processed yet</span>
                </div>
              ) : (
                candidates.filter(c => c.stage === "hired").map((c) => (
                  <div key={c.id} className="bg-emerald-50/50 border border-emerald-200 rounded-xl p-3 shadow-sm transition">
                    <div className="flex items-center gap-1.5">
                      <IconCircleCheck size={15} className="text-emerald-600" />
                      <h4 className="font-bold text-gray-900 text-[13px]">{c.name}</h4>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 pl-5">{c.role}</p>
                    <div className="mt-2.5 pl-5">
                      <span className="inline-block text-[9px] bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full font-bold">
                        Ready for Onboarding
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      )}

      {/* -------------------- 3. SCREENING HUB -------------------- */}
      {activeSubView === "screening" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Candidates list (1/3) */}
          <div className="lg:col-span-4 bg-white border border-gray-100 rounded-2xl shadow-sm p-4 h-[600px] flex flex-col">
            <h3 className="font-bold text-sm text-gray-700 mb-3 uppercase tracking-wide">Candidates in Screening</h3>
            
            <div className="space-y-2 flex-grow overflow-y-auto pr-1">
              {candidates.filter(c => c.stage === "screening").length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20 text-gray-400">
                  <IconSearch size={32} className="text-gray-300 mb-1" />
                  <span className="text-xs">No candidates pending screening</span>
                </div>
              ) : (
                candidates.filter(c => c.stage === "screening").map((c) => (
                  <div
                    key={c.id}
                    onClick={() => setSelectedCandidateId(c.id)}
                    className={`p-3.5 rounded-xl border cursor-pointer transition ${
                      selectedCandidateId === c.id
                        ? "border-[#FBD037] bg-[#FBD037]/5 shadow-sm"
                        : "border-gray-100 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="font-bold text-[13.5px] text-gray-900">{c.name}</h4>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                        {c.matchScore}% Match
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{c.role}</div>
                    <div className="flex gap-1.5 mt-2.5">
                      {c.skills.slice(0, 3).map((s, i) => (
                        <span key={i} className="text-[8px] bg-gray-100 px-1.5 py-0.5 rounded-md text-gray-600">{s}</span>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Screening Review Form (2/3) */}
          <div className="lg:col-span-8 bg-white border border-gray-100 rounded-2xl shadow-sm p-6 min-h-[600px] flex flex-col">
            {(() => {
              const activeCandidate = candidates.find(c => c.id === selectedCandidateId && c.stage === "screening");
              if (!activeCandidate) {
                return (
                  <div className="flex-grow flex flex-col items-center justify-center text-center py-20 text-gray-400">
                    <div className="w-16 h-16 bg-yellow-50 rounded-full border border-yellow-100 flex items-center justify-center mb-4">
                      <IconSparkles className="text-[#FBD037]" size={28} />
                    </div>
                    <h3 className="font-bold text-[#212121] text-base mb-1">AI Resume Match Screening</h3>
                    <p className="text-xs max-w-sm">Select a candidate from the list to evaluate details, checklist requirements, and approve for interview loop.</p>
                  </div>
                );
              }

              const checklist = activeCandidate.screeningChecklist || { skillsMatch: false, salaryFit: false, locationReady: false, communicationReady: false };

              return (
                <div className="space-y-5 flex-grow flex flex-col">
                  {/* Candidate Quick Header */}
                  <div className="flex justify-between items-start border-b border-gray-100 pb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-900">{activeCandidate.name}</h2>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center gap-0.5">
                          <IconSparkles size={11} className="text-emerald-600 animate-pulse" />
                          {activeCandidate.matchScore}% Match
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{activeCandidate.role} — parsed via {activeCandidate.source}</p>
                    </div>
                    <div className="text-right text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
                      <div><strong className="text-gray-900">Email:</strong> {activeCandidate.email}</div>
                      <div className="mt-0.5"><strong className="text-gray-900">Phone:</strong> {activeCandidate.phone}</div>
                    </div>
                  </div>

                  {/* Skills Parser Grid */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Resume Key Skills matching</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-emerald-50/50 border border-emerald-100 rounded-xl">
                        <span className="text-[10px] font-bold text-emerald-700 block mb-1">Matched in Job Profile</span>
                        <div className="flex flex-wrap gap-1.5">
                          {activeCandidate.skills.map((s, i) => (
                            <span key={i} className="text-[10px] bg-white border border-emerald-200 text-emerald-800 px-2 py-0.5 rounded-md flex items-center gap-0.5">
                              <IconCheck size={9} /> {s}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="p-3 bg-amber-50/40 border border-amber-100 rounded-xl">
                        <span className="text-[10px] font-bold text-amber-700 block mb-1">Core Experience</span>
                        <div className="text-xs text-gray-600 leading-relaxed font-medium">
                          Found 4+ years related to {activeCandidate.role}. Solid history of project contributions matching technical description.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Screening checklist */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Screening Verification Checklist</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        { label: "Technical Skills Verified", key: "skillsMatch" as const },
                        { label: "CTC / Salary Expectation Fit", key: "salaryFit" as const },
                        { label: "Location & Mode Alignment", key: "locationReady" as const },
                        { label: "Communication Skills Check", key: "communicationReady" as const },
                      ].map((item) => (
                        <label 
                          key={item.key}
                          className="flex items-center justify-between p-3 border border-gray-100 hover:border-gray-200 rounded-xl cursor-pointer bg-white transition shadow-sm"
                        >
                          <span className="text-xs text-gray-700 font-medium">{item.label}</span>
                          <input
                            type="checkbox"
                            checked={checklist[item.key]}
                            onChange={() => updateScreeningChecklist(activeCandidate.id, item.key)}
                            className="w-4.5 h-4.5 accent-[#FBD037] border-gray-300 rounded focus:ring-[#FBD037] cursor-pointer"
                          />
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Screening review notes */}
                  <div className="flex-grow space-y-1.5">
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Screening Evaluation Notes</h4>
                    <textarea
                      rows={3}
                      placeholder="Add key insights, red flags, or specific things to check during the technical loop..."
                      value={activeCandidate.screeningNotes || ""}
                      onChange={(e) => updateScreeningNotes(activeCandidate.id, e.target.value)}
                      className="w-full p-3 rounded-xl border border-gray-200 focus:border-[#FBD037] outline-none text-xs leading-relaxed"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-3 border-t border-gray-100 justify-end">
                    <button
                      onClick={() => {
                        handleTransitionCandidate(activeCandidate.id, "rejected");
                        toast.error(`Candidate ${activeCandidate.name} has been rejected.`);
                      }}
                      className="px-4 py-2 border border-gray-200 hover:bg-red-50 hover:border-red-200 text-red-600 font-bold rounded-xl text-xs transition flex items-center gap-1.5"
                    >
                      <IconCircleX size={15} /> Reject Candidate
                    </button>
                    <button
                      onClick={() => {
                        handleTransitionCandidate(activeCandidate.id, "interview", "interviews");
                        toast.success(`Screening approved! Candidate ${activeCandidate.name} moved to Interview Loop.`);
                      }}
                      className="px-5 py-2.5 bg-[#FBD037] hover:bg-[#FACC15] text-[#212121] font-bold rounded-xl text-xs shadow-sm transition flex items-center gap-1.5"
                    >
                      <IconCircleCheck size={15} /> Approve & Schedule Interview
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* -------------------- 4. INTERVIEW LOOP HUB -------------------- */}
      {activeSubView === "interviews" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Candidates list (1/3) */}
          <div className="lg:col-span-4 bg-white border border-gray-100 rounded-2xl shadow-sm p-4 h-[600px] flex flex-col">
            <h3 className="font-bold text-sm text-gray-700 mb-3 uppercase tracking-wide">Candidates in Interview</h3>
            <div className="space-y-2 flex-grow overflow-y-auto pr-1">
              {candidates.filter(c => c.stage === "interview").length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20 text-gray-400">
                  <IconCalendar size={32} className="text-gray-300 mb-1" />
                  <span className="text-xs">No candidates pending interview loop</span>
                </div>
              ) : (
                candidates.filter(c => c.stage === "interview").map((c) => (
                  <div
                    key={c.id}
                    onClick={() => setSelectedCandidateId(c.id)}
                    className={`p-3.5 rounded-xl border cursor-pointer transition ${
                      selectedCandidateId === c.id
                        ? "border-[#FBD037] bg-[#FBD037]/5 shadow-sm"
                        : "border-gray-100 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="font-bold text-[13.5px] text-gray-900">{c.name}</h4>
                      <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${
                        c.interviewDetails?.status === "Scheduled"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}>
                        {c.interviewDetails?.status || "Pending Setup"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{c.role}</div>
                    
                    {c.interviewDetails?.date && (
                      <div className="text-[9px] text-amber-800 mt-2 bg-amber-50 px-2 py-0.5 rounded border border-amber-100 inline-block font-mono">
                        {c.interviewDetails.date} @ {c.interviewDetails.time}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Interview Details & Feedback (2/3) */}
          <div className="lg:col-span-8 bg-white border border-gray-100 rounded-2xl shadow-sm p-6 min-h-[600px] flex flex-col">
            {(() => {
              const activeCandidate = candidates.find(c => c.id === selectedCandidateId && c.stage === "interview");
              if (!activeCandidate) {
                return (
                  <div className="flex-grow flex flex-col items-center justify-center text-center py-20 text-gray-400">
                    <div className="w-16 h-16 bg-yellow-50 rounded-full border border-yellow-100 flex items-center justify-center mb-4">
                      <IconCalendar className="text-[#FBD037]" size={28} />
                    </div>
                    <h3 className="font-bold text-[#212121] text-base mb-1">Interview Loop Coordinator</h3>
                    <p className="text-xs max-w-sm">Select an active candidate to book their interview round, create meeting rooms, or record grader feedback score.</p>
                  </div>
                );
              }

              const details = activeCandidate.interviewDetails || {
                date: "", time: "", type: "Zoom", round: "Technical Round 1", interviewer: "", link: "", status: "Pending"
              };

              const isScheduled = details.status === "Scheduled";

              return (
                <div className="space-y-4 flex-grow flex flex-col">
                  
                  {/* Candidate Quick Header */}
                  <div className="flex justify-between items-start border-b border-gray-100 pb-3">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">{activeCandidate.name}</h2>
                      <p className="text-xs text-gray-500 mt-0.5">{activeCandidate.role} — Pipeline Intake via {activeCandidate.source}</p>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <div>{activeCandidate.email}</div>
                      <div>{activeCandidate.phone}</div>
                    </div>
                  </div>

                  {/* Scheduled state card or Scheduling form */}
                  {!isScheduled ? (
                    <div className="space-y-4 bg-gray-50/50 p-4 rounded-2xl border border-gray-100">
                      <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1">
                        <span className="w-1.5 h-3 bg-[#FBD037] inline-block rounded-sm"></span>
                        Schedule Interview Loop
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Interview Round</label>
                          <select 
                            value={details.round} 
                            onChange={(e) => handleUpdateInterviewField(activeCandidate.id, "round", e.target.value)}
                            className="w-full p-2 border border-gray-200 bg-white rounded-xl text-xs outline-none focus:border-[#FBD037]"
                          >
                            <option>Technical Round 1: Coding</option>
                            <option>Technical Round 2: Architecture</option>
                            <option>Management & Values Fit</option>
                            <option>HR Final & Salary Discussion</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Assigned Interviewer</label>
                          <input 
                            type="text" 
                            placeholder="e.g. Amit Patel (Tech Lead)" 
                            value={details.interviewer} 
                            onChange={(e) => handleUpdateInterviewField(activeCandidate.id, "interviewer", e.target.value)}
                            className="w-full p-2 border border-gray-200 bg-white rounded-xl text-xs outline-none focus:border-[#FBD037]"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Date</label>
                          <input 
                            type="date" 
                            value={details.date} 
                            onChange={(e) => handleUpdateInterviewField(activeCandidate.id, "date", e.target.value)}
                            className="w-full p-2 border border-gray-200 bg-white rounded-xl text-xs outline-none focus:border-[#FBD037] font-mono"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Time</label>
                          <input 
                            type="time" 
                            value={details.time} 
                            onChange={(e) => handleUpdateInterviewField(activeCandidate.id, "time", e.target.value)}
                            className="w-full p-2 border border-gray-200 bg-white rounded-xl text-xs outline-none focus:border-[#FBD037] font-mono"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Meeting Platform</label>
                          <select 
                            value={details.type} 
                            onChange={(e) => handleUpdateInterviewField(activeCandidate.id, "type", e.target.value as any)}
                            className="w-full p-2 border border-gray-200 bg-white rounded-xl text-xs outline-none focus:border-[#FBD037]"
                          >
                            <option value="Zoom">Zoom Meeting</option>
                            <option value="Google Meet">Google Meet</option>
                            <option value="In-Person">In-Person at Office</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Meeting Link / Room</label>
                          <div className="flex gap-2">
                            <input 
                              type="text" 
                              placeholder="Generate or enter URL" 
                              value={details.link} 
                              onChange={(e) => handleUpdateInterviewField(activeCandidate.id, "link", e.target.value)}
                              className="flex-grow p-2 border border-gray-200 bg-white rounded-xl text-xs outline-none focus:border-[#FBD037]"
                            />
                            {details.type !== "In-Person" && (
                              <button
                                type="button"
                                disabled={isGeneratingLink}
                                onClick={() => handleGenerateMeetingLink(activeCandidate.id)}
                                className="bg-[#212121] text-white hover:bg-gray-800 text-[10px] font-bold px-3 py-2 rounded-xl transition flex items-center justify-center gap-1 shrink-0"
                              >
                                {isGeneratingLink ? <IconLoader2 size={13} className="animate-spin" /> : <IconVideo size={13} />}
                                Generate Meet
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="pt-2 flex justify-end">
                        <button
                          onClick={() => handleScheduleInterview(activeCandidate.id)}
                          className="bg-[#FBD037] hover:bg-[#FACC15] text-[#212121] font-bold px-5 py-2 rounded-xl text-xs shadow-sm transition flex items-center gap-1"
                        >
                          <IconCalendar size={14} /> Schedule & Dispatch Invites
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Scheduled loop status details */
                    <div className="space-y-4">
                      <div className="bg-emerald-50/50 border border-emerald-200/80 p-4 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                        <div className="flex gap-3 items-start">
                          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700 shrink-0">
                            <IconVideo size={20} />
                          </div>
                          <div>
                            <span className="text-[10px] uppercase font-bold text-emerald-800 tracking-wider">Scheduled {details.type} Loop</span>
                            <h3 className="font-bold text-sm text-gray-900 mt-0.5">{details.round}</h3>
                            <p className="text-xs text-gray-600 font-medium font-mono mt-0.5">
                              {details.date} at {details.time} | Inter: {details.interviewer}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2 w-full md:w-auto">
                          {details.link && (
                            <a 
                              href={details.link} 
                              target="_blank" 
                              rel="noreferrer"
                              className="flex-1 md:flex-none text-center bg-[#212121] text-white hover:bg-gray-800 text-xs font-bold px-4 py-2 rounded-xl transition flex items-center justify-center gap-1"
                            >
                              Join Call <IconExternalLink size={13} />
                            </a>
                          )}
                          <button
                            onClick={() => handleUpdateInterviewField(activeCandidate.id, "status", "Pending")}
                            className="bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-bold px-3 py-2 rounded-xl transition"
                          >
                            Reschedule
                          </button>
                        </div>
                      </div>

                      {/* Grading and feedback card */}
                      <div className="border border-gray-100 p-4 rounded-2xl space-y-4">
                        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1">
                          <span className="w-1.5 h-3 bg-[#FBD037] inline-block rounded-sm"></span>
                          Interviewer Feedback & Score
                        </h3>

                        <div className="space-y-3">
                          {/* Star rating selector */}
                          <div>
                            <span className="block text-[10px] font-bold text-gray-500 uppercase mb-1.5">Rating Score</span>
                            <div className="flex items-center gap-1.5">
                              {[1, 2, 3, 4, 5].map((score) => {
                                const active = (details.feedbackScore || 0) >= score;
                                return (
                                  <button
                                    key={score}
                                    onClick={() => handleUpdateInterviewField(activeCandidate.id, "feedbackScore", score)}
                                    className="transition transform hover:scale-110"
                                  >
                                    <IconStar 
                                      size={22} 
                                      className={active ? "text-amber-500 fill-amber-400" : "text-gray-300 hover:text-amber-400"}
                                    />
                                  </button>
                                );
                              })}
                              {details.feedbackScore && (
                                <span className="text-xs font-bold text-amber-700 ml-2 font-mono">{details.feedbackScore} / 5</span>
                              )}
                            </div>
                          </div>

                          {/* Notes */}
                          <div>
                            <span className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Evaluation Details</span>
                            <textarea
                              rows={3}
                              placeholder="Enter interview feedback, technical strength details, coding assessment score, communication observations..."
                              value={details.feedbackNotes || ""}
                              onChange={(e) => handleUpdateInterviewField(activeCandidate.id, "feedbackNotes", e.target.value)}
                              className="w-full p-3 rounded-xl border border-gray-200 focus:border-[#FBD037] outline-none text-xs leading-relaxed"
                            />
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-between pt-2 border-t border-gray-100">
                          <button
                            onClick={() => {
                              handleTransitionCandidate(activeCandidate.id, "rejected");
                              toast.error(`${activeCandidate.name} has failed the loop.`);
                            }}
                            className="px-4 py-2 border border-gray-200 hover:bg-red-50 hover:border-red-200 text-red-600 font-bold rounded-xl text-xs transition flex items-center gap-1"
                          >
                            <IconCircleX size={14} /> Fail candidate
                          </button>
                          
                          <button
                            onClick={() => {
                              if (!details.feedbackScore) {
                                toast.error("Please assign a feedback score first");
                                return;
                              }
                              handleTransitionCandidate(activeCandidate.id, "offer", "offers");
                              toast.success(`Candidate passed interview! Moved to Offer stage.`);
                            }}
                            className="bg-[#FBD037] hover:bg-[#FACC15] text-[#212121] font-bold px-5 py-2.5 rounded-xl text-xs shadow-sm transition flex items-center gap-1"
                          >
                            Recommend & Generate Offer <IconChevronRight size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* -------------------- 5. OFFER GENERATION HUB -------------------- */}
      {activeSubView === "offers" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Candidates list (1/3) */}
          <div className="lg:col-span-3 bg-white border border-gray-100 rounded-2xl shadow-sm p-4 h-[650px] flex flex-col">
            <h3 className="font-bold text-sm text-gray-700 mb-3 uppercase tracking-wide">Pending Offers</h3>
            <div className="space-y-2 flex-grow overflow-y-auto pr-1">
              {candidates.filter(c => c.stage === "offer").length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20 text-gray-400">
                  <IconFileText size={32} className="text-gray-300 mb-1" />
                  <span className="text-xs">No pending offer processes</span>
                </div>
              ) : (
                candidates.filter(c => c.stage === "offer").map((c) => (
                  <div
                    key={c.id}
                    onClick={() => setSelectedCandidateId(c.id)}
                    className={`p-3.5 rounded-xl border cursor-pointer transition ${
                      selectedCandidateId === c.id
                        ? "border-[#FBD037] bg-[#FBD037]/5 shadow-sm"
                        : "border-gray-100 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="font-bold text-[13.5px] text-gray-900">{c.name}</h4>
                      <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${
                        c.offerDetails?.status === "Sent" 
                          ? "bg-blue-50 text-blue-700 border-blue-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}>
                        {c.offerDetails?.status || "Draft"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{c.role}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Offer Form + Document Preview Grid (9/12 split internally) */}
          <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-12 gap-4 h-[650px]">
            {(() => {
              const activeCandidate = candidates.find(c => c.id === selectedCandidateId && c.stage === "offer");
              if (!activeCandidate) {
                return (
                  <div className="col-span-12 bg-white border border-gray-100 rounded-2xl shadow-sm p-6 flex flex-col items-center justify-center text-center text-gray-400">
                    <div className="w-16 h-16 bg-yellow-50 rounded-full border border-yellow-100 flex items-center justify-center mb-4">
                      <IconFileText className="text-[#FBD037]" size={28} />
                    </div>
                    <h3 className="font-bold text-[#212121] text-base mb-1">Offer Letter generator</h3>
                    <p className="text-xs max-w-sm">Select a candidate in offer stage to enter package details, joining date, and review dynamic generated letter preview.</p>
                  </div>
                );
              }

              const offer = activeCandidate.offerDetails || {
                ctc: "120,000", joiningDate: "", manager: "Rajesh Kumar (VP Eng)", probation: "3 Months", template: "Standard", status: "Draft", language: "english", currency: "SAR"
              };

              const offerLang = offer.language || "english";
              const offerCurr = offer.currency || "SAR";

              // Calculate salary details
              const parsedCtc = parseFloat(offer.ctc.replace(/,/g, "")) || 0;
              const basic = Math.round(parsedCtc * 0.5);
              const hra = Math.round(parsedCtc * 0.2);
              const special = Math.round(parsedCtc * 0.2);
              const pf = Math.round(parsedCtc * 0.1);

              const isArabic = offerLang === "arabic";
              const isBilingual = offerLang === "bilingual";
              const isEnglish = offerLang === "english";

              const currSym = offerCurr === "SAR" ? "SAR" : "₹";
              const currSymAr = offerCurr === "SAR" ? "ر.س" : "ر.هـ";

              // Arabic Translation Mapping
              const arTrans = {
                title: "عرض عمل - " + activeCandidate.role,
                ref: "المرجع",
                date: "التاريخ",
                dear: `عزيزي ${activeCandidate.name}،`,
                joiningDate: "تاريخ الالتحاق بالعمل",
                manager: "المدير المسؤول",
                probation: "فترة التجربة",
                salaryDetails: "تفاصيل الراتب والمزايا",
                component: "بند الراتب",
                monthly: `الراتب الشهري (${offerCurr === "SAR" ? "ر.س" : "INR"})`,
                annual: `الراتب السنوي (${offerCurr === "SAR" ? "ر.س" : "INR"})`,
                basic: "الراتب الأساسي (50%)",
                hra: "بدل السكن (20%)",
                special: offerCurr === "SAR" ? "بدل انتقال واتصال (20%)" : "بدل خاص (20%)",
                pf: offerCurr === "SAR" ? "التأمينات الاجتماعية GOSI (10%)" : "صندوق الادخار / التأمين (10%)",
                total: "إجمالي الراتب الممنوح (CTC)",
                managerSign: "مدير الموارد البشرية",
                acceptedSign: "توقيع المرشح بالقبول",
                standardIntro: "يسعدنا أن نقدم لك عرض عمل لدى شركة جوارم للخدمات اللوجستية. لقد أعجبنا كثيراً بمؤهلاتك ونتطلع إلى انضمامك لفريقنا.",
                techIntro: "مرحباً بك في فريق الهندسة التقنية! في جوارم، نقوم ببناء أنظمة متطورة تدير الخدمات اللوجستية في جميع أنحاء المملكة. يسعدنا دعوتك للمشاركة في صياغة مستقبل تكنولوجيا سلاسل الإمداد.",
                execIntro: "نيابة عن مجلس الإدارة، يسعدني أن أقدم لك هذا المنصب القيادي الاستراتيجي. نحن على ثقة بأن خبرتكم ستكون محورية في تنفيذ رؤيتنا للنمو وتحقيق التميز المؤسسي.",
                body: `سيتم تعيينك في وظيفة (${activeCandidate.role}) تحت إشراف المدير المسؤول (${offer.manager || "راجيش كومار"}). تاريخ الالتحاق بالعمل هو (${offer.joiningDate || "يحدد لاحقاً"}). ستكون خاضعاً لفترة تجربة مدتها (${offer.probation}) تبدأ من تاريخ مباشرة العمل، ويتم بعدها تقييم الأداء لتثبيت العقد.`
              };

              const getArabicIntro = (template: string) => {
                if (template === "Tech") return arTrans.techIntro;
                if (template === "Executive") return arTrans.execIntro;
                return arTrans.standardIntro;
              };

              const getEnglishIntro = (template: string) => {
                if (template === "Tech") return "Welcome to the Engineering team! At GWARM, we build cutting-edge systems that power logistics across the country. We are thrilled to invite you to join us in shaping the future of supply chain technology.";
                if (template === "Executive") return "On behalf of the Board of Directors, I am pleased to offer you this strategic leadership position. We believe your expertise will be instrumental in executing our growth vision and driving organisational excellence.";
                return "We are pleased to offer you employment with GWARM Logistics. We were extremely impressed by your credentials and look forward to welcoming you to our team.";
              };

              return (
                <>
                  {/* Form config panel (5/12 columns) */}
                  <div className="md:col-span-5 bg-white border border-gray-100 rounded-2xl shadow-sm p-4 flex flex-col overflow-y-auto space-y-4">
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1">
                      <span className="w-1.5 h-3 bg-[#FBD037] inline-block rounded-sm"></span>
                      Configure Salary & Terms
                    </h3>

                    <div className="space-y-3 flex-grow">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Official Designation</label>
                        <input 
                          type="text" 
                          value={activeCandidate.role} 
                          disabled
                          className="w-full p-2 border border-gray-100 bg-gray-50 text-gray-500 rounded-xl text-xs font-medium outline-none"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Language Option</label>
                          <select 
                            value={offer.language || "english"} 
                            onChange={(e) => handleUpdateOfferField(activeCandidate.id, "language", e.target.value)}
                            className="w-full p-2 border border-gray-200 bg-white rounded-xl text-xs outline-none focus:border-[#FBD037]"
                          >
                            <option value="english">English Only</option>
                            <option value="arabic">Arabic Only (العربية)</option>
                            <option value="bilingual">Bilingual (English & العربية)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Currency</label>
                          <select 
                            value={offer.currency || "SAR"} 
                            onChange={(e) => handleUpdateOfferField(activeCandidate.id, "currency", e.target.value)}
                            className="w-full p-2 border border-gray-200 bg-white rounded-xl text-xs outline-none focus:border-[#FBD037]"
                          >
                            <option value="SAR">Saudi Riyal (SAR / ر.س)</option>
                            <option value="INR">Indian Rupee (INR / ₹)</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Annual CTC ({currSym})</label>
                        <input 
                          type="text" 
                          value={offer.ctc} 
                          onChange={(e) => handleUpdateOfferField(activeCandidate.id, "ctc", e.target.value)}
                          placeholder={offerCurr === "SAR" ? "e.g. 150,000" : "e.g. 18,00,000"}
                          className="w-full p-2 border border-gray-200 rounded-xl text-xs outline-none focus:border-[#FBD037] font-mono"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Joining Date</label>
                        <input 
                          type="date" 
                          value={offer.joiningDate} 
                          onChange={(e) => handleUpdateOfferField(activeCandidate.id, "joiningDate", e.target.value)}
                          className="w-full p-2 border border-gray-200 rounded-xl text-xs outline-none focus:border-[#FBD037] font-mono"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Reporting Manager</label>
                        <input 
                          type="text" 
                          value={offer.manager} 
                          onChange={(e) => handleUpdateOfferField(activeCandidate.id, "manager", e.target.value)}
                          placeholder="Reporting Manager Name"
                          className="w-full p-2 border border-gray-200 rounded-xl text-xs outline-none focus:border-[#FBD037]"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Probation Period</label>
                        <select 
                          value={offer.probation} 
                          onChange={(e) => handleUpdateOfferField(activeCandidate.id, "probation", e.target.value)}
                          className="w-full p-2 border border-gray-200 bg-white rounded-xl text-xs outline-none focus:border-[#FBD037]"
                        >
                          <option>3 Months</option>
                          <option>6 Months</option>
                          <option>Direct Hire (No Probation)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Offer Template Theme</label>
                        <select 
                          value={offer.template} 
                          onChange={(e) => handleUpdateOfferField(activeCandidate.id, "template", e.target.value)}
                          className="w-full p-2 border border-gray-200 bg-white rounded-xl text-xs outline-none focus:border-[#FBD037]"
                        >
                          <option value="Standard">Standard Corporate Offer</option>
                          <option value="Tech">Tech / Developer Focus Template</option>
                          <option value="Executive">Executive Leadership Template</option>
                        </select>
                      </div>
                    </div>

                    {/* Left Actions */}
                    <div className="pt-2 border-t border-gray-100 space-y-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDownloadOfferPdf(activeCandidate.name)}
                          className="flex-1 py-2 border border-gray-200 hover:bg-gray-50 font-bold rounded-xl text-xs transition flex items-center justify-center gap-1.5 text-gray-700"
                        >
                          <IconDownload size={14} /> Download PDF
                        </button>
                        <button
                          disabled={isSendingOffer}
                          onClick={() => handleSendOfferEmail(activeCandidate.id)}
                          className="flex-1 bg-[#212121] text-white hover:bg-gray-800 font-bold py-2 rounded-xl text-xs transition flex items-center justify-center gap-1.5 shadow-sm"
                        >
                          {isSendingOffer ? <IconLoader2 size={14} className="animate-spin" /> : <IconSend size={14} />}
                          Email Candidate
                        </button>
                      </div>
                      
                      {offer.status === "Sent" && (
                        <button
                          disabled={isAcceptingOffer}
                          onClick={() => handleAcceptOfferMock(activeCandidate.id)}
                          className="w-full bg-[#FBD037] hover:bg-[#FACC15] text-[#212121] font-bold py-2.5 rounded-xl text-xs transition flex items-center justify-center gap-1.5 shadow-md"
                        >
                          {isAcceptingOffer ? <IconLoader2 size={14} className="animate-spin" /> : <IconCircleCheck size={14} />}
                          Simulate Candidate Accept
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Document preview panel (7/12 columns) */}
                  <div className="md:col-span-7 bg-[#EFEFEF] rounded-2xl p-4 flex flex-col h-full overflow-hidden shadow-inner border border-gray-200">
                    <div className="text-[10px] text-gray-500 font-mono mb-2 flex items-center justify-between px-1">
                      <span>DOCUMENT PREVIEW (LIVE UPDATING)</span>
                      <span>STATUS: <span className="font-bold text-gray-800">{offer.status.toUpperCase()}</span></span>
                    </div>

                    {/* Paper Document Representation */}
                    <div className="bg-white flex-grow rounded-xl shadow-md border border-gray-200 p-5 overflow-y-auto text-gray-800 leading-relaxed relative flex flex-col min-h-[500px]">
                      
                      {/* Bilingual Header Layout */}
                      <div className="border-b-2 border-amber-400 pb-3">
                        {isBilingual ? (
                          <div className="flex justify-between items-center text-center">
                            <div className="text-left" dir="rtl">
                              <div className="flex items-center gap-1 font-sans font-extrabold text-[10.5px] text-gray-900 tracking-wider">
                                شركة جوارم للخدمات اللوجستية
                                <span className="w-3 h-3 bg-[#FBD037] rounded-md inline-block mr-1"></span>
                              </div>
                              <span className="text-[6.5px] text-gray-400 font-sans block">المكتب الرئيسي: قطاع ٣، الرياض، المملكة العربية السعودية</span>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center gap-1 font-sans font-extrabold text-[10.5px] text-gray-900 tracking-wider justify-end">
                                <span className="w-3 h-3 bg-[#FBD037] rounded-md inline-block"></span>
                                GWARM LOGISTICS PVT. LTD.
                              </div>
                              <span className="text-[6.5px] text-gray-400 font-sans block">Corporate Office: Sector 3, Pune, India / Riyadh, KSA</span>
                            </div>
                          </div>
                        ) : isArabic ? (
                          <div className="text-center flex flex-col items-center" dir="rtl">
                            <div className="flex items-center gap-1 font-sans font-extrabold text-[12px] text-gray-900 tracking-wider">
                              شركة جوارم للخدمات اللوجستية م.م.
                              <span className="w-3.5 h-3.5 bg-[#FBD037] rounded-md inline-block mr-1.5"></span>
                            </div>
                            <span className="text-[7px] text-gray-400 font-sans mt-0.5">المكتب الرئيسي: طريق باهر، قطاع ٣، الرياض، المملكة العربية السعودية</span>
                          </div>
                        ) : (
                          <div className="text-center flex flex-col items-center">
                            <div className="flex items-center gap-1 font-sans font-extrabold text-[12px] text-gray-900 tracking-wider">
                              <span className="w-3.5 h-3.5 bg-[#FBD037] rounded-md inline-block flex-shrink-0"></span>
                              GWARM LOGISTICS PVT. LTD.
                            </div>
                            <span className="text-[7px] text-gray-400 font-sans mt-0.5">Corporate Office: Baner Road, Sector 3, Pune, MH, India / Riyadh, KSA</span>
                          </div>
                        )}
                      </div>

                      {/* Letter Metadata */}
                      <div className={`flex justify-between mt-3 font-sans text-[7.5px] text-gray-500 ${isArabic ? "flex-row-reverse" : ""}`}>
                        <span>Ref: GWARM/HR/OFFER-2026/{activeCandidate.id}</span>
                        <span>التاريخ / Date: {new Date().toLocaleDateString(isArabic ? "ar-SA" : "en-IN")}</span>
                      </div>

                      {/* Bilingual Recipient Details */}
                      <div className={`mt-3 flex justify-between gap-4 border-b border-gray-100 pb-2`}>
                        {isBilingual ? (
                          <>
                            <div className="text-left font-sans text-[8px]" dir="rtl">
                              <strong className="text-gray-900 block text-[9.5px]">{activeCandidate.name}</strong>
                              <span className="text-gray-400">{activeCandidate.email} | {activeCandidate.phone}</span>
                            </div>
                            <div className="border-r border-gray-100 h-6"></div>
                            <div className="text-right font-sans text-[8px]">
                              <strong className="text-gray-900 block text-[9.5px]">{activeCandidate.name}</strong>
                              <span className="text-gray-400">{activeCandidate.email} | {activeCandidate.phone}</span>
                            </div>
                          </>
                        ) : isArabic ? (
                          <div className="w-full text-right font-sans text-[8px]" dir="rtl">
                            <strong className="text-gray-900 block text-[9.5px]">{activeCandidate.name}</strong>
                            <span className="text-gray-400">{activeCandidate.email} | {activeCandidate.phone}</span>
                          </div>
                        ) : (
                          <div className="w-full text-left font-sans text-[8px]">
                            <strong className="text-gray-900 block text-[9.5px]">{activeCandidate.name}</strong>
                            <span className="text-gray-400">{activeCandidate.email} | {activeCandidate.phone}</span>
                          </div>
                        )}
                      </div>

                      {/* Title */}
                      <div className="mt-3 text-center font-sans font-extrabold text-[9px] underline text-gray-900 uppercase">
                        {isBilingual ? "عرض عمل / Letter of Offer" : isArabic ? "خطاب عرض عمل" : "Letter of Offer"}
                      </div>

                      {/* Bilingual Body Columns */}
                      <div className={`mt-3 grid gap-4 text-[8px] leading-relaxed text-justify ${
                        isBilingual ? "grid-cols-2" : "grid-cols-1"
                      }`}>
                        
                        {/* Left Column (Arabic if Bilingual, else English if English-only) */}
                        {isBilingual ? (
                          <div className="font-sans text-right" dir="rtl">
                            <p>{arTrans.dear}</p>
                            <p className="mt-1.5">
                              {getArabicIntro(offer.template)} سيتم تعيينك في وظيفة <strong className="text-gray-900">{activeCandidate.role}</strong> تحت إشراف <strong className="text-gray-900">{offer.manager || "المدير المسؤول"}</strong>.
                            </p>
                            <p className="mt-1">
                              تاريخ الالتحاق المقرر هو <strong className="text-gray-900">{offer.joiningDate || "يحدد لاحقاً"}</strong>. ستخضع لفترة تجربة مدتها <strong className="text-gray-900">{offer.probation}</strong> تبدأ من تاريخ مباشرتك للعمل.
                            </p>
                          </div>
                        ) : isArabic ? (
                          <div className="font-sans text-right" dir="rtl">
                            <p>{arTrans.dear}</p>
                            <p className="mt-1.5">
                              {getArabicIntro(offer.template)} سيتم تعيينك في وظيفة <strong className="text-gray-900">{activeCandidate.role}</strong> تحت إشراف <strong className="text-gray-900">{offer.manager || "المدير المسؤول"}</strong>.
                            </p>
                            <p className="mt-1">
                              تاريخ الالتحاق المقرر هو <strong className="text-gray-900">{offer.joiningDate || "يحدد لاحقاً"}</strong>. ستخضع لفترة تجربة مدتها <strong className="text-gray-900">{offer.probation}</strong> تبدأ من تاريخ مباشرتك للعمل.
                            </p>
                          </div>
                        ) : (
                          <div className="font-serif">
                            <p>Dear {activeCandidate.name},</p>
                            <p className="mt-1.5">
                              {getEnglishIntro(offer.template)} You will be appointed in the role of <strong className="text-gray-900">{activeCandidate.role}</strong> reporting to <strong className="text-gray-900">{offer.manager || "Rajesh Kumar (VP Eng)"}</strong>.
                            </p>
                            <p className="mt-1">
                              Your joining date is scheduled as <strong className="text-gray-900">{offer.joiningDate || "TBD"}</strong>. You will undergo a probation period of <strong className="text-gray-900">{offer.probation}</strong> from your joining date.
                            </p>
                          </div>
                        )}

                        {/* Right Column (English if Bilingual, else hidden if mono-lingual) */}
                        {isBilingual && (
                          <div className="font-serif">
                            <p>Dear {activeCandidate.name},</p>
                            <p className="mt-1.5">
                              {getEnglishIntro(offer.template)} You will be appointed in the role of <strong className="text-gray-900">{activeCandidate.role}</strong> reporting to <strong className="text-gray-900">{offer.manager || "Rajesh Kumar (VP Eng)"}</strong>.
                            </p>
                            <p className="mt-1">
                              Your joining date is scheduled as <strong className="text-gray-900">{offer.joiningDate || "TBD"}</strong>. You will undergo a probation period of <strong className="text-gray-900">{offer.probation}</strong> from your joining date.
                            </p>
                          </div>
                        )}

                      </div>

                      {/* Salary Breakup Table */}
                      <div className="mt-3 font-sans">
                        <strong className={`text-[7.5px] uppercase tracking-wide text-gray-700 block mb-1 ${isArabic ? "text-right" : ""}`}>
                          {isBilingual ? "تفاصيل الراتب / Salary Breakup" : isArabic ? "تفاصيل الراتب والمزايا" : "Salary Breakup details"}
                        </strong>
                        
                        <table className="w-full text-left border-collapse border border-gray-200 text-[8px]" dir={isArabic ? "rtl" : "ltr"}>
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 font-bold">
                              <th className="p-1 border-r border-gray-200">
                                {isBilingual ? "بند الراتب / Salary Component" : isArabic ? "بند الراتب" : "Salary Component"}
                              </th>
                              <th className={`p-1 border-r border-gray-200 text-right`}>
                                {isBilingual ? "شهري / Monthly" : isArabic ? `الراتب الشهري (${currSymAr})` : `Monthly (${currSym})`}
                              </th>
                              <th className="p-1 text-right">
                                {isBilingual ? "سنوي / Annual" : isArabic ? `الراتب السنوي (${currSymAr})` : `Annual (${currSym})`}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="font-mono text-[7.5px]">
                            <tr className="border-b border-gray-100">
                              <td className="p-1 border-r border-gray-200 font-sans">
                                {isBilingual ? "الراتب الأساسي / Basic Pay" : isArabic ? "الراتب الأساسي (50%)" : "Basic Pay (50%)"}
                              </td>
                              <td className="p-1 text-right border-r border-gray-200">{Math.round(basic / 12).toLocaleString(isArabic ? "ar-SA" : "en-IN")}</td>
                              <td className="p-1 text-right">{basic.toLocaleString(isArabic ? "ar-SA" : "en-IN")}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="p-1 border-r border-gray-200 font-sans">
                                {isBilingual ? "بدل السكن / Housing Allowance" : isArabic ? "بدل السكن (20%)" : "Housing Allowance (20%)"}
                              </td>
                              <td className="p-1 text-right border-r border-gray-200">{Math.round(hra / 12).toLocaleString(isArabic ? "ar-SA" : "en-IN")}</td>
                              <td className="p-1 text-right">{hra.toLocaleString(isArabic ? "ar-SA" : "en-IN")}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="p-1 border-r border-gray-200 font-sans">
                                {isBilingual ? "بدل الانتقال / Special & Transport Allowance" : isArabic ? "بدل انتقال (20%)" : "Special Allowance (20%)"}
                              </td>
                              <td className="p-1 text-right border-r border-gray-200">{Math.round(special / 12).toLocaleString(isArabic ? "ar-SA" : "en-IN")}</td>
                              <td className="p-1 text-right">{special.toLocaleString(isArabic ? "ar-SA" : "en-IN")}</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                              <td className="p-1 border-r border-gray-200 font-sans">
                                {isBilingual 
                                  ? (offerCurr === "SAR" ? "التأمينات الاجتماعية / GOSI Social Insurance" : "صندوق الادخار / Provident Fund") 
                                  : (offerCurr === "SAR" ? "GOSI Social Insurance (10%)" : "Provident Fund (10%)")}
                              </td>
                              <td className="p-1 text-right border-r border-gray-200">{Math.round(pf / 12).toLocaleString(isArabic ? "ar-SA" : "en-IN")}</td>
                              <td className="p-1 text-right">{pf.toLocaleString(isArabic ? "ar-SA" : "en-IN")}</td>
                            </tr>
                            <tr className="bg-amber-50 font-bold border-b border-gray-200 text-gray-900">
                              <td className="p-1 border-r border-gray-200 font-sans">
                                {isBilingual ? "إجمالي الراتب / Total Compensation (CTC)" : isArabic ? "إجمالي الراتب الممنوح" : "Total Compensation (CTC)"}
                              </td>
                              <td className="p-1 text-right border-r border-gray-200">{Math.round(parsedCtc / 12).toLocaleString(isArabic ? "ar-SA" : "en-IN")}</td>
                              <td className="p-1 text-right">{parsedCtc.toLocaleString(isArabic ? "ar-SA" : "en-IN")}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Bilingual Signatures Layout */}
                      <div className="mt-auto pt-6 flex justify-between font-sans text-[7.5px] items-end">
                        
                        {/* Manager Signature Left */}
                        <div className="text-left">
                          <div className="h-5 flex items-end">
                            <span className="font-mono text-gray-400 italic text-[6.5px]">DigiSign_GWARM_KSA</span>
                          </div>
                          <div className="border-t border-gray-400 w-24 pt-1 font-bold text-gray-800">
                            {isBilingual ? "مدير الموارد البشرية / HR Manager" : isArabic ? "مدير الموارد البشرية" : "HR Manager"}
                          </div>
                          <div className="text-gray-400 text-[6px]">
                            {isBilingual ? "جوارم للخدمات اللوجستية / GWARM Logistics" : isArabic ? "جوارم للخدمات اللوجستية" : "GWARM Logistics"}
                          </div>
                        </div>

                        {/* Candidate Signature Right */}
                        <div className="text-right">
                          <div className="h-5"></div>
                          <div className="border-t border-gray-400 w-24 pt-1 font-bold text-gray-800 ml-auto">
                            {isBilingual ? "التوقيع بالقبول / Accepted by" : isArabic ? "قبول المرشح والتوقيع" : "Accepted by"}
                          </div>
                          <div className="text-gray-400 text-[6px]">
                            {isBilingual ? "توقيع الموظف / Candidate Signature" : isArabic ? "توقيع الموظف" : "Candidate Signature"}
                          </div>
                        </div>

                      </div>

                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* -------------------- 6. CREATE JOB MODAL -------------------- */}
      {isCreateJobOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-xl p-6 shadow-2xl border border-gray-100 flex flex-col space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-1.5">
                <IconBriefcase size={18} className="text-[#FBD037]" />
                Create New Job Requisition
              </h2>
              <button 
                onClick={() => setIsCreateJobOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <IconX size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="md:col-span-2">
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Job Title</label>
                <input 
                  type="text" 
                  placeholder="e.g. Senior React Developer" 
                  value={newJobTitle}
                  onChange={(e) => setNewJobTitle(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl outline-none focus:border-[#FBD037] text-xs font-semibold"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Department</label>
                <select 
                  value={newJobDept} 
                  onChange={(e) => setNewJobDept(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 bg-white rounded-xl outline-none focus:border-[#FBD037] text-xs font-medium"
                >
                  <option>Engineering</option>
                  <option>Human Resources</option>
                  <option>IT & Support</option>
                  <option>Sales & Operations</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Target Location</label>
                <select 
                  value={newJobLoc} 
                  onChange={(e) => setNewJobLoc(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 bg-white rounded-xl outline-none focus:border-[#FBD037] text-xs font-medium"
                >
                  <option>Baner Branch</option>
                  <option>Main Head Office</option>
                  <option>Remote</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Salary Range</label>
                <input 
                  type="text" 
                  placeholder="e.g. ₹12L - ₹18L" 
                  value={newJobSalary}
                  onChange={(e) => setNewJobSalary(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl outline-none focus:border-[#FBD037] text-xs font-semibold"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Job Description</label>
                <textarea 
                  rows={3}
                  placeholder="Enter high-level requirements..." 
                  value={newJobDesc}
                  onChange={(e) => setNewJobDesc(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl outline-none focus:border-[#FBD037] text-xs leading-relaxed"
                />
              </div>

              {/* Posting Channels Select */}
              <div className="md:col-span-2">
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2">Publish to Channels</label>
                <div className="flex flex-wrap gap-2">
                  {CHANNELS.map((ch) => {
                    const selected = newJobChannels.includes(ch);
                    return (
                      <button
                        type="button"
                        key={ch}
                        onClick={() => {
                          if (selected) {
                            setNewJobChannels(prev => prev.filter(c => c !== ch));
                          } else {
                            setNewJobChannels(prev => [...prev, ch]);
                          }
                        }}
                        className={`px-3 py-2 rounded-xl text-xs font-bold border transition ${
                          selected 
                            ? "bg-[#FBD037] border-[#FBD037] text-[#212121] shadow-sm"
                            : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                        }`}
                      >
                        {ch}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2.5 pt-3 border-t border-gray-100">
              <button 
                onClick={() => setIsCreateJobOpen(false)}
                className="px-4 py-2 border border-gray-200 hover:bg-gray-50 rounded-xl text-xs font-bold text-gray-500 transition"
              >
                Cancel
              </button>
              <button 
                onClick={triggerCreateJob}
                className="bg-[#FBD037] hover:bg-[#FACC15] text-[#212121] font-bold px-5 py-2 rounded-xl text-xs shadow-sm transition"
              >
                Post Requisition
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------------------- 7. MULTI-CHANNEL POSTING LOADER MODAL -------------------- */}
      {isPostingJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 shadow-2xl border border-gray-100 text-center flex flex-col items-center space-y-4">
            <div className="relative w-16 h-16 flex items-center justify-center bg-yellow-50 rounded-full border border-yellow-100">
              <IconLoader2 className="text-[#FBD037] animate-spin" size={32} />
            </div>
            
            <div>
              <h3 className="font-extrabold text-sm text-gray-900">Broadcasting Job Posting</h3>
              <p className="text-xs text-gray-500 mt-0.5">Syncing with external candidate intake boards...</p>
            </div>

            <div className="w-full space-y-2.5 text-left border-t border-b border-gray-100 py-4 text-xs font-medium text-gray-600">
              <div className="flex items-center justify-between">
                <span>1. Creating Requisition Record</span>
                {postingStep >= 1 ? <IconCircleCheck className="text-emerald-500" size={16} /> : <span className="w-4 h-4 rounded-full border border-gray-200 inline-block"></span>}
              </div>
              <div className="flex items-center justify-between">
                <span>2. Syncing to LinkedIn Jobs</span>
                {postingStep >= 2 ? <IconCircleCheck className="text-emerald-500" size={16} /> : <span className="w-4 h-4 rounded-full border border-gray-200 inline-block"></span>}
              </div>
              <div className="flex items-center justify-between">
                <span>3. Syndicating to Indeed Employers</span>
                {postingStep >= 3 ? <IconCircleCheck className="text-emerald-500" size={16} /> : <span className="w-4 h-4 rounded-full border border-gray-200 inline-block"></span>}
              </div>
              <div className="flex items-center justify-between">
                <span>4. Indexing on Naukri portal</span>
                {postingStep >= 4 ? <IconCircleCheck className="text-emerald-500" size={16} /> : <span className="w-4 h-4 rounded-full border border-gray-200 inline-block"></span>}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
